import { ReactNode, useMemo } from 'react'
import { GridColumn, RowId, SortState } from '../../types/grid.types'
import { useGridSort } from '../../hooks/useGridSort'
import { useGridPagination } from '../../hooks/useGridPagination'
import Panel from '../Panel/Panel'
import Toolbar from '../Toolbar/Toolbar'
import TableScroll from './TableScroll'
import GridHead from './GridHead'
import GridBody from './GridBody'
import GridFooter from '../Footer/GridFooter'

export interface GridTableProps<T> {
  columns: GridColumn<T>[]
  rows: T[]
  getRowId: (row: T) => RowId
  isLoading?: boolean
  onRowClick?: (row: T) => void

  /** Toolbar */
  title?: ReactNode
  count?: ReactNode
  toolbarActions?: ReactNode

  /** Sorting */
  initialSort?: SortState<T>

  /** Pagination */
  paginated?: boolean
  perPageOptions?: number[]
  initialPerPage?: number

  /** Empty state */
  emptyTitle?: string
  emptySubtitle?: string
  emptyIcon?: ReactNode
}

const DEFAULT_PER_PAGE_OPTIONS = [50, 100, 150]

export const GridTable = <T,>({
  columns,
  rows,
  getRowId,
  isLoading,
  onRowClick,
  title,
  count,
  toolbarActions,
  initialSort = null,
  paginated = true,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  initialPerPage = 50,
  emptyTitle,
  emptySubtitle,
  emptyIcon,
}: GridTableProps<T>) => {
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible !== false),
    [columns],
  )

  const { sortState, sortedRows, toggleSort } = useGridSort<T>({
    rows,
    columns: visibleColumns,
    initial: initialSort,
  })

  const pagination = useGridPagination<T>({
    rows: sortedRows,
    initialPerPage,
  })

  const bodyRows = paginated ? pagination.pageRows : sortedRows
  const showFooter = paginated && !isLoading
  const hasToolbar = title != null || count != null || toolbarActions != null

  return (
    <Panel>
      {hasToolbar && (
        <Toolbar title={title} count={count ?? rows.length}>
          {toolbarActions}
        </Toolbar>
      )}

      <TableScroll>
        <table>
          <GridHead columns={visibleColumns} sortState={sortState} onSort={toggleSort} />
          <GridBody
            columns={visibleColumns}
            rows={bodyRows}
            getRowId={getRowId}
            isLoading={isLoading}
            onRowClick={onRowClick}
            emptyTitle={emptyTitle}
            emptySubtitle={emptySubtitle}
            emptyIcon={emptyIcon}
          />
        </table>
      </TableScroll>

      {showFooter && (
        <GridFooter
          rangeStart={pagination.rangeStart}
          rangeEnd={pagination.rangeEnd}
          total={pagination.total}
          page={pagination.page}
          pageCount={pagination.pageCount}
          perPage={pagination.perPage}
          perPageOptions={perPageOptions}
          onPage={pagination.setPage}
          onPerPage={pagination.setPerPage}
        />
      )}
    </Panel>
  )
}

export default GridTable
